import { Injectable, signal } from '@angular/core';
import * as Auth from 'aws-amplify/auth';
import { AuthSession, type UpdateUserAttributesOutput } from 'aws-amplify/auth';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { environment } from '@environment';
import { catchError, tap } from 'rxjs/operators';
import { User } from '../../pages/main/users/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  token = signal<string | undefined>(undefined);
  sub = signal<string | undefined>(undefined, {
    equal: (a, b) => a === b,
  });

  authenticated = signal<boolean>(false);

  // Track token expiration
  private tokenExpiration = new BehaviorSubject<Date | null>(null);
  private refreshInProgress = false;

  /**
   * Get the authentication token, refreshing it if necessary
   */
  getAuthToken(): Observable<AuthSession> {
    // Check if token is expired or about to expire
    const expiration = this.tokenExpiration.value;
    const now = new Date();

    // Get refresh window from environment config or use default (5 minutes)
    const refreshBeforeExpiryMinutes = environment.security?.tokenRefresh?.refreshBeforeExpiryMinutes || 5;
    const refreshThreshold = new Date(now.getTime() + refreshBeforeExpiryMinutes * 60 * 1000);

    if (expiration && expiration < refreshThreshold && !this.refreshInProgress) {
      return this.refreshToken();
    }

    return from(Auth.fetchAuthSession()).pipe(
      tap((session: AuthSession) => {
        if (session?.tokens?.accessToken) {
          this.token.set(session.tokens.accessToken.toString());
          this.sub.set(session.tokens.accessToken.payload.sub);
          this.authenticated.set(true);

          // Store token expiration time
          if (session.tokens.accessToken.payload.exp) {
            const expirationTime = new Date(session.tokens.accessToken.payload.exp * 1000);
            this.tokenExpiration.next(expirationTime);
          }
        }
      }),
      catchError(error => {
        console.error('Error fetching auth session:', error);
        return throwError(() => new Error('Authentication failed'));
      })
    );
  }

  /**
   * Refresh the authentication token
   */
  private refreshToken(): Observable<AuthSession> {
    this.refreshInProgress = true;

    return from(Auth.fetchAuthSession({ forceRefresh: true })).pipe(
      tap((session: AuthSession) => {
        if (session?.tokens?.accessToken) {
          this.token.set(session.tokens.accessToken.toString());
          this.sub.set(session.tokens.accessToken.payload.sub);

          // Update token expiration time
          if (session.tokens.accessToken.payload.exp) {
            const expirationTime = new Date(session.tokens.accessToken.payload.exp * 1000);
            this.tokenExpiration.next(expirationTime);
          }
        }
        this.refreshInProgress = false;
      }),
      catchError(error => {
        this.refreshInProgress = false;
        console.error('Error refreshing token:', error);
        return throwError(() => new Error('Token refresh failed'));
      })
    );
  }

  logout(): Observable<void> {
    // Clear user data
    this.token.set(undefined);
    this.sub.set(undefined);
    this.tokenExpiration.next(null);
    this.authenticated.set(false);

    return from(Auth.signOut());
  }

  updateUser(user: User): Promise<UpdateUserAttributesOutput> {
    return Auth.updateUserAttributes({
      userAttributes: {
        family_name: user.lastName,
        given_name: user.firstName,
      },
    });
  }
}
